import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Article (Transformation, mapped to the "Educational" goal token).
 * The maturity journey from paper/Excel to a live twin: a process-flow of the
 * stages plus before/after comparison bars (latency of insight, data trust).
 * Honest, vendor-neutral body; product only appears in the closing block.
 * All numbers are illustrative.
 */
export const fromClipboardsToALivePlantModel: Article = {
  slug: "from-clipboards-to-a-live-plant-model",
  title: "From clipboards and spreadsheets to a live plant model",
  deck:
    "Most foundries do not lack data — they lack data they can trust, in time to act on it. The journey from paper logs to a living model is not a single leap. It is a handful of stages, each of which earns the next.",
  goal: "Educational",
  readingMins: 8,
  heroStat: { value: "days → seconds", label: "how long it takes a question to get an answer" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "Walk almost any foundry floor and you will find the same quiet ritual: an operator with a clipboard, noting a furnace temperature or a sand result into a column that will, at the end of the shift, be keyed into a spreadsheet that someone else will try to make sense of next week. There is nothing wrong with the people in this picture. The data is being captured diligently. The problem is what happens to it afterwards — by the time it could answer a question, the heat it described is long cold, the casting has shipped, and the moment to act has passed. The journey to a live plant model is, more than anything, a journey to close that gap.",
    },
    {
      kind: "paragraph",
      text:
        "It helps to see this as a maturity curve rather than a switch you flip. Plants do not go from paper to a digital twin overnight, and the ones that try usually stall. The path has recognisable stages, and each stage is worth standing on for a while because it fixes a specific failure of the one before it. You can locate your own operation on this curve, and the next step is almost always the obvious one.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The maturity curve",
      heading: "Four stages from clipboard to live model",
      sub: "Each stage removes a delay or a doubt that the previous one left in place.",
    },
    {
      kind: "processFlow",
      title: "The journey, stage by stage",
      steps: [
        {
          title: "Paper & clipboards",
          detail:
            "Readings are captured by hand on the floor. Honest, but trapped — the data lives in a binder no analysis can reach.",
          icon: "search",
        },
        {
          title: "Spreadsheets",
          detail:
            "Numbers are keyed in after the shift. Now they are searchable, but stale, manually transcribed, and scattered across files.",
          icon: "boxes",
        },
        {
          title: "Dashboards",
          detail:
            "Live tags stream onto screens. You finally see the plant in real time — but the numbers still float free of the process.",
          icon: "gauge",
        },
        {
          title: "Live plant model",
          detail:
            "Every reading lands on the entity that produced it, with history beneath it. The plant becomes a thing you can question.",
          icon: "gitBranch",
        },
      ],
      caption:
        "The leap that matters most is the last one: from numbers on a screen to numbers attached to the process that made them.",
    },
    {
      kind: "paragraph",
      text:
        "Notice that the first three stages all share a hidden flaw. Paper is honest but unreachable. Spreadsheets are reachable but stale and hand-copied — every transcription is a chance to introduce an error, and nobody can quite vouch for the figure six rows down. Dashboards fix the staleness but not the meaning: a wall of live gauges tells you that the pour temperature is 1432, but not that it belongs to the heat that filled the moulds that became the batch a customer just rejected. The model is what finally ties the number to its place in the process — and only then can you reason from effect back to cause.",
    },
    {
      kind: "pullQuote",
      quote:
        "A spreadsheet remembers what you typed. A live model remembers what actually happened — and can tell you why it mattered.",
      attribution: "The difference a model makes",
    },
    {
      kind: "sectionHeading",
      eyebrow: "What changes",
      heading: "Two things improve at every step: speed, and trust",
      sub: "Latency of insight collapses; confidence in the number climbs.",
    },
    {
      kind: "paragraph",
      text:
        "Two measures track the whole journey. The first is the latency of insight — how long it takes between a question being asked and an answer you can act on. On paper that is measured in days; once the model is live it is measured in seconds. The second is data trust — how much you believe the figure in front of you. Hand-transcribed spreadsheets are notoriously soft here, because every value passed through a tired hand at the end of a shift. Data read straight off the equipment and stamped onto the model is simply more believable, and a number you trust is a number you will act on.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: latency of insight, before vs. after a live model",
      unit: "hours to an answer",
      max: 72,
      bars: [
        { label: "Clipboards & binders", value: 72, tone: "danger", display: "~3 days" },
        { label: "End-of-shift spreadsheets", value: 24, tone: "warning", display: "~1 day" },
        { label: "Live dashboards", value: 2, tone: "accent", display: "~2 hours" },
        { label: "Live plant model", value: 1, tone: "success", display: "seconds–minutes" },
      ],
      caption:
        "Generic, illustrative figures — not a specific plant's audit. The shape is the point: the gap between a question and an answer collapses as you climb the curve.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: how much you trust the number in front of you",
      unit: "/ 10 confidence",
      max: 10,
      bars: [
        { label: "Hand-written logs", value: 4, tone: "danger" },
        { label: "Manually keyed spreadsheets", value: 5, tone: "warning" },
        { label: "Live dashboards", value: 8, tone: "accent" },
        { label: "Model-attached, machine-read data", value: 9, tone: "success" },
      ],
      caption:
        "Qualitative, illustrative ranking. Trust rises sharply once the human transcription step is removed and each value carries its context.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Dashboards are not the destination",
      text:
        "It is easy to mistake a screen full of live gauges for the finish line. But a dashboard with no model behind it still cannot answer 'which heat caused this?' — it shows the present clearly and forgets the past. A twin is a dashboard that also has a memory and a map.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "How to move",
      heading: "You climb one rung at a time, on one line",
    },
    {
      kind: "paragraph",
      text:
        "The encouraging part is that you do not have to skip stages or boil the ocean. The sensible move is always the next rung, on a single line: if you are on spreadsheets, get one line's critical tags streaming live; if you have dashboards, attach those tags to a model of that line so the numbers gain context and history. Each rung pays for itself before you climb the next, and because the work is narrow it stays low-risk. The plants that succeed treat this as a series of small, useful steps — not a transformation programme that has to be right all at once.",
    },
    {
      kind: "chart",
      chart: "line",
      title: "Illustrative: time-to-answer as a plant climbs the maturity curve",
      xKey: "stage",
      unit: "h",
      series: [{ dataKey: "hours", label: "Hours to a trustworthy answer", tone: "brand" }],
      data: [
        { stage: "Paper", hours: 72 },
        { stage: "Spreadsheet", hours: 24 },
        { stage: "Dashboard", hours: 2 },
        { stage: "Model", hours: 0.1 },
      ],
      caption:
        "Generic, illustrative curve. The collapse toward the right is the whole reason the journey is worth taking.",
    },
    {
      kind: "paragraph",
      text:
        "The clipboard is not the enemy — it was the right tool when there was nothing better to carry the data. But it traps what it captures, and the spreadsheet that follows only half-frees it. A live model is what turns all that diligent recording into something you can actually interrogate: fast enough to matter, trustworthy enough to act on, and connected enough to point at a cause. That is the transformation worth making, and you make it one rung at a time.",
    },
    {
      kind: "sahasEnables",
      intro:
        "Climbing from spreadsheets to a live model is a sound plan in the abstract; the friction is usually in the doing — wiring the tags, building the model, keeping the history. That is the part the platform is built to make routine.",
      capabilities: [
        {
          title: "Stand up the model fast",
          detail:
            "Lay out your line as a connected process graph, or start from a ready-wired plant template and adjust — no blank-page project.",
          icon: "gitBranch",
        },
        {
          title: "Replace the clipboard",
          detail:
            "A self-hosted gateway streams readings straight off existing equipment, so values are machine-read and stamped onto the model — not transcribed by hand.",
          icon: "radio",
        },
        {
          title: "Keep the memory",
          detail:
            "Every reading and batch is retained and tied to the entity that produced it, so 'what was different last Tuesday?' becomes a query, not an archaeology dig.",
          icon: "database",
        },
      ],
    },
  ],
};
